import {Form, Input, Select, Switch} from 'antd';
import React from "react";
import {IFieldOptionsEditorProps} from "../FieldOptionsEditor";
import DirectoryPicker from "../../../../../shared/pickers/file/DirectoryPicker";
import {IFilePickerWidgetType} from "../../../../../shared/pickers/file/FilePicker";
import LocaleText from "../../../../settings/dictionary/LocaleText";

export interface IFieldFileOptions {
    fileWidget?: IFilePickerWidgetType
    fileCanChoose?: boolean
    fileCanUpload?: boolean
    fileCanDownload?: boolean
    fileCanRemove?: boolean
    fileMultiple?: boolean
    fileDisabled?: boolean
    fileDirectory?: number
    fileExtensions?: string[]
    fileModalPicker?: boolean
}

const EXTENSION_OPTIONS = [
    // --- GLOBAL BUNDLES ---
    { label: 'All Files (No Filter)', value: '*' },
    { label: 'All Images', value: 'image/*' },
    { label: 'All Video', value: 'video/*' },
    { label: 'All Audio', value: 'audio/*' },

    // --- DOCUMENTS (Office & Productivity) ---
    { label: 'PDF Document', value: 'application/pdf' },
    { label: 'Word (Modern) .docx', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    { label: 'Word (Legacy) .doc', value: 'application/msword' },
    { label: 'Excel (Modern) .xlsx', value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    { label: 'Excel (Legacy) .xls', value: 'application/vnd.ms-excel' },
    { label: 'Excel Binary .xlsb', value: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12' },
    { label: 'PowerPoint (Modern) .pptx', value: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' },
    { label: 'PowerPoint (Legacy) .ppt', value: 'application/vnd.ms-powerpoint' },
    { label: 'OpenDocument Text .odt', value: 'application/vnd.oasis.opendocument.text' },
    { label: 'OpenDocument Spreadsheet .ods', value: 'application/vnd.oasis.opendocument.spreadsheet' },
    { label: 'Rich Text .rtf', value: 'application/rtf' },
    { label: 'Plain Text .txt', value: 'text/plain' },
    { label: 'CSV Data .csv', value: 'text/csv' },

    // --- IMAGES (Web, Print & Pro) ---
    { label: 'JPEG Image', value: 'image/jpeg' },
    { label: 'PNG Image', value: 'image/png' },
    { label: 'GIF Animation', value: 'image/gif' },
    { label: 'WebP Next-Gen', value: 'image/webp' },
    { label: 'SVG Vector', value: 'image/svg+xml' },
    { label: 'TIFF (High Res)', value: 'image/tiff' },
    { label: 'Adobe Photoshop .psd', value: '.psd,image/vnd.adobe.photoshop' },
    { label: 'Adobe Illustrator .ai', value: '.ai,application/postscript' },
    { label: 'Adobe InDesign .indd', value: '.indd,application/x-indesign' },
    { label: 'CorelDRAW .cdr', value: '.cdr,application/coreldraw' },
    { label: 'Icon File .ico', value: 'image/x-icon' },
    { label: 'HEIC (Apple Photos)', value: 'image/heic' },
    { label: 'RAW Camera Image', value: '.raw,.cr2,.nef,.dng' },
    { label: 'PostScript .eps', value: 'application/postscript,.eps' },

    // --- WEB & SOFTWARE DEVELOPMENT (Code) ---
    { label: 'HTML Webpage', value: 'text/html' },
    { label: 'CSS Stylesheet', value: 'text/css' },
    { label: 'JavaScript .js', value: 'text/javascript,application/javascript' },
    { label: 'TypeScript .ts', value: 'video/mp2t,.ts' }, // Note: .ts is shared with MPEG transport streams
    { label: 'JSON Data', value: 'application/json' },
    { label: 'XML Data', value: 'application/xml,text/xml' },
    { label: 'PHP Script', value: '.php,text/x-php' },
    { label: 'Python Script .py', value: '.py,text/x-python' },
    { label: 'Java Source .java', value: 'text/x-java-source' },
    { label: 'C++ Source .cpp', value: '.cpp,text/x-c' },
    { label: 'C# Source .cs', value: '.cs,text/plain' },
    { label: 'Ruby Script .rb', value: '.rb,text/x-ruby' },
    { label: 'Markdown .md', value: 'text/markdown,.md' },
    { label: 'YAML Config', value: '.yaml,.yml,text/yaml' },
    { label: 'SQL Database Script', value: '.sql,text/x-sql' },
    { label: 'Shell Script .sh', value: 'application/x-sh' },
    { label: 'DockerFile', value: '.dockerfile' },
    { label: 'Log File .log', value: 'text/plain,.log' },

    // --- ARCHIVES & INSTALLERS ---
    { label: 'ZIP Archive', value: 'application/zip' },
    { label: 'RAR Archive', value: 'application/vnd.rar' },
    { label: '7-Zip Archive', value: 'application/x-7z-compressed' },
    { label: 'Tarball .tar.gz', value: 'application/x-gtar' },
    { label: 'Gzip .gz', value: 'application/gzip' },
    { label: 'Windows Installer .msi', value: 'application/x-msi' },
    { label: 'Executable .exe', value: 'application/x-msdownload' },
    { label: 'Apple Disk Image .dmg', value: 'application/x-apple-diskimage' },
    { label: 'ISO Disk Image', value: 'application/x-iso9660-image' },

    // --- VIDEO PRODUCTION ---
    { label: 'MP4 Video', value: 'video/mp4' },
    { label: 'QuickTime Movie .mov', value: 'video/quicktime' },
    { label: 'AVI Video', value: 'video/x-msvideo' },
    { label: 'Matroska .mkv', value: 'video/x-matroska' },
    { label: 'WebM Video', value: 'video/webm' },
    { label: 'WMV (Windows Media)', value: 'video/x-ms-wmv' },
    { label: 'Flash Video .flv', value: 'video/x-flv' },
    { label: 'Subtitle File .srt', value: '.srt,text/plain' },

    // --- AUDIO & MUSIC ---
    { label: 'MP3 Audio', value: 'audio/mpeg' },
    { label: 'WAV (Lossless)', value: 'audio/wav' },
    { label: 'FLAC (Lossless)', value: 'audio/flac' },
    { label: 'AAC Audio', value: 'audio/aac' },
    { label: 'MIDI Music', value: 'audio/midi' },
    { label: 'M4A (Apple Audio)', value: 'audio/mp4' },
    { label: 'OGG Audio', value: 'audio/ogg' },

    // --- ENGINEERING & SCIENTIFIC (The Hard Ones) ---
    { label: 'AutoCAD .dwg', value: '.dwg,image/vnd.dwg' },
    { label: 'AutoCAD DXF .dxf', value: '.dxf,image/vnd.dxf' },
    { label: '3D Step File .step', value: '.step,.stp' },
    { label: 'STL (3D Print)', value: '.stl' },
    { label: 'SketchUp .skp', value: '.skp' },
    { label: 'Revit .rvt', value: '.rvt' },
    { label: 'Google Earth .kml', value: 'application/vnd.google-earth.kml+xml' },
    { label: 'GPX (GPS Exchange)', value: '.gpx,application/gpx+xml' },
    { label: 'DICOM (Medical)', value: 'application/dicom' },
    { label: 'SQLite DB', value: '.sqlite,application/x-sqlite3' },

    // --- FONTS ---
    { label: 'TrueType Font .ttf', value: 'font/ttf' },
    { label: 'OpenType Font .otf', value: 'font/otf' },
    { label: 'Web Font .woff', value: 'font/woff' },
    { label: 'Web Font .woff2', value: 'font/woff2' },

    // --- MISC & EBOOKS ---
    { label: 'EPUB Ebook', value: 'application/epub+zip' },
    { label: 'Kindle .mobi', value: 'application/x-mobipocket-ebook' },
    { label: 'VCard (Contact)', value: 'text/vcard' },
    { label: 'Calendar .ics', value: 'text/calendar' },
    { label: 'Certificate .crt', value: 'application/x-x509-ca-cert' },
    { label: 'Binary Data .bin', value: 'application/octet-stream' }
];

interface IProps extends IFieldOptionsEditorProps {

}

interface IState {
}

class FieldFileOptionsEditor extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {}
    }

    static getDefaultOptions(): IFieldFileOptions {
        return {
            fileCanChoose: true,
            fileCanDownload: false,
            fileCanRemove: false,
            fileCanUpload: true,
            fileWidget: 'simple'
        }
    }

    render() {
        const {buildFieldName} = this.props

        const types: { code: IFilePickerWidgetType, label: string }[] = [
            {code: "simple", label: 'Jednoduchý'},
            {code: "extended", label: 'Rozšířený'}
        ]

        const switches = [
            {label: 'Nahrání', code: 'fileCanUpload', initialValue: true},
            {label: 'Prohlížeč', code: 'fileCanChoose', initialValue: false},
            {label: 'Stahování', code: 'fileCanDownload', initialValue: false},
            {label: 'Odstranění', code: 'fileCanRemove', initialValue: false},
            {label: 'Nahrání modal', code: 'fileModalPicker', initialValue: false}
        ]

        return (
            <div>
                <Form.Item name={buildFieldName('fileWidget')} initialValue={'simple'}
                           label={'Vyberte režim výběru souborů'} rules={[{required: true}]}>
                    <Select>
                        {types.map(type => (
                            <Select.Option key={type.code} value={type.code}>{type.label}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label={'Možnosti'}>
                    <Input.Group>
                        {switches.map(switchItem => (
                            <Form.Item name={buildFieldName(switchItem.code)} initialValue={switchItem.initialValue}
                                       valuePropName={'checked'} noStyle>
                                <Switch className={'mr-2'}
                                        unCheckedChildren={switchItem.label}
                                        checkedChildren={switchItem.label}
                                />
                            </Form.Item>
                        ))}
                    </Input.Group>
                </Form.Item>
                <Form.Item name={buildFieldName('fileExtensions')} label={<LocaleText code={'configuration.field.file.extensions'} fallback={'Povolené typy souborů'}/>}>
                    <Select
                        showSearch
                        placeholder="Search 100+ formats..."
                        mode={'multiple'}
                        optionFilterProp="label" // Crucial: allows searching by the label text
                        style={{ width: '100%' }}
                        options={EXTENSION_OPTIONS}
                    />
                </Form.Item>
                <Form.Item name={buildFieldName('fileDirectory')} label={'Omezit na jednu složku'}>
                    <DirectoryPicker defaultRoot={false}/>
                </Form.Item>
            </div>
        )
    }
}


export default FieldFileOptionsEditor